<script setup>
import { reactive, ref, unref, toRaw, computed, provide } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import ArrowDownSvg from '@/assets/arrow-down.svg?skipsvgo'
import { useMediaFilterStore } from '../stores/mediaFilterStore'
import { storeToRefs } from 'pinia'


const props = defineProps({
    storeKey: { type: String, default: 'options' },
    synced: { type: Boolean, default: false },
    placeholder: { type: String, required: true },
    dynamicPlaceholder: { type: Boolean, default: true },
})

const emit = defineEmits(['change'])

const isOpen = ref(false)

let options;
const store = useMediaFilterStore()
if (props.synced) {
    options = storeToRefs(store)[props.storeKey]
} else {
    options = ref([])
}

const selectedOption = computed(() => options.value.find(({ selected }) => selected))
const computedValue = computed(() => {
    if (!props.dynamicPlaceholder) { return props.placeholder }

    if (selectedOption.value == null | selectedOption.value?.value == undefined) {
        return props.placeholder
    }
    return selectedOption.value.label ?? selectedOption.value.value
});

function registerOption(option) {
    options.value.push(option)
}
function unregisterOption(option) {
    options.value = options.value.filter(({ uid }) => uid !== option.uid)
}
function select(uid) {
    options.value.forEach((option) => {
        if (option.uid === uid) {
            option.selected = true
        } else {
            option.selected = false
        }
    })
    emit('change', toRaw(unref(selectedOption)));
    close()
}
provide('dropdown', { select, registerOption, unregisterOption });
function open()   { isOpen.value = true }
function close()  { isOpen.value = false }
function toggle() { isOpen.value = !isOpen.value }
</script>

<template>
    <div
        v-on-click-outside="close"
        :class="['dropdown', { 'dropdown--open': isOpen }]"
    >
        <div
            class="dropdown__header"
            @click="toggle"
        >
        <span>{{ computedValue }}</span>
        <ArrowDownSvg class="dropdown__icon" />
    </div>

        <div class="dropdown__dropdown">
            <transition name="drop-in">
                <div
                    v-show="isOpen"
                    class="dropdown__options"
                >
                    <slot></slot>
                </div>
            </transition>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/scss/framework" as *;

@layer components.dropdown {

    :root,
    .theme--turquoise-blue {
        --dropdown-color: var(--color-turquoise-blue);
        --dropdown-color--hover: var(--color-turquoise-blue--hover);
        --dropdown-icon-color: var(--color-turquoise-blue);
        --dropdown-icon-color--hover: var(--color-turquoise-blue--hover);
        --dropdown-open-icon-color: var(--color-turquoise-blue);
        --dropdown-open-icon-color--hover: var(--color-turquoise-blue--hover);
        --dropdown-options-background-color: var(--color-black);
        --dropdown-option-color: var(--color-white);
        --dropdown-option-color--hover: var(--color-cinder);
        --dropdown-option-background-color--hover: var(--color-turquoise-blue);
        --dropdown-selected-color: var(--color-cinder);
        --dropdown-selected-background-color: var(--color-turquoise-blue);
        --dropdown-selected-background-color--hover: var(--color-turquoise-blue--hover);
    }

    .theme--radical-red {
        --dropdown-color: var(--color-radical-red);
        --dropdown-color--hover: var(--color-radical-red--hover);
        --dropdown-icon-color: var(--color-radical-red);
        --dropdown-icon-color--hover: var(--color-radical-red--hover);
        --dropdown-open-icon-color: var(--color-radical-red);
        --dropdown-open-icon-color--hover: var(--color-radical-red--hover);
        --dropdown-options-background-color: var(--color-black);
        --dropdown-option-color: var(--color-white);
        --dropdown-option-color--hover: var(--color-cinder);
        --dropdown-option-background-color--hover: var(--color-radical-red);
        --dropdown-selected-color: var(--color-cinder);
        --dropdown-selected-background-color: var(--color-radical-red);
        --dropdown-selected-background-color--hover: var(--color-radical-red--hover);
    }

    .dropdown {
        --_transition: var(--duration-slow) var(--easing-default);

        display: inline-flex;
        flex-flow: column;
        align-items: flex-start;
        gap: 0;
        position: relative;
        padding: 0;
        font: var(--font-body-1);

        &__header {
            position: relative;
            cursor: pointer;
            color: var(--dropdown-color);
            display: inline-flex;
            align-items: center;
            gap: var(--size-6);
            z-index: 1;
            transition: color var(--_transition);


            .dropdown &:hover {
                @media (hover: hover) and (pointer: fine) {
                    color: var(--dropdown-color--hover);

                    .dropdown__icon {
                        color: var(--dropdown-icon-color--hover);
                    }
                }
            }
            .dropdown--open &:hover {
                @media (hover: hover) and (pointer: fine) {
                    .dropdown__icon {
                        color: var(--dropdown-open-icon-color--hover);
                    }
                }
            }
        }

        &__dropdown {
            position: absolute;
            left: 0;
            top: 100%;
        }

        &__icon {
            width: auto;
            height: 0.384em;
            width: 0.6865em;
            transition:
                transform var(--_transition),
                color var(--_transition)
            ;
            color: var(--dropdown-icon-color);

            .dropdown--open & {
                color: var(--dropdown-open-icon-color);
            }

            @include svg-auto-color;
        }

        &__options {
            position: relative;
            z-index: 2;
            background-color: var(--dropdown-options-background-color);

            display: flex;
            flex-flow: column;
        }

        &__option {
            cursor: pointer;
            padding: var(--size-6) var(--size-16);
            white-space: nowrap;
            color: var(--dropdown-option-color);

            @include hover {
                color: var(--dropdown-option-color--hover);
                background-color: var(--dropdown-option-background-color--hover);
            }

            &-selected {
                color: var(--dropdown-selected-color);
                background-color: var(--dropdown-selected-background-color);
                @include hover {
                    background-color: var(--dropdown-selected-background-color--hover);
                }
            }
        }

        &--open {
            .dropdown__header { }
            .dropdown__icon {
                transform: rotateZ(180deg);
            }
        }
    }

    .drop-in-enter-active {
        transform-origin: top;
        animation: drop-in var(--duration-slow) var(--easing-decelerate) forwards;
    }

    .drop-in-leave-active {
        transform-origin: top;
        animation: drop-out var(--duration-slow) var(--easing-decelerate) forwards;
    }

    @keyframes drop-in {
        0% { opacity: 0; transform: rotateX(90deg); }
        20% { opacity: 1; }
        100% { opacity: 1; transform: rotateX(0deg); }
    }

    @keyframes drop-out {
        0% { opacity: 1; transform: rotateX(0deg); }
        80% { opacity: 1; }
        100% { opacity: 0; transform: rotateX(90deg); }
    }
}

</style>
